import { useEffect, useState, useRef } from "react";
import { ChevronRight, ChevronLeft, X, HelpCircle } from "lucide-react";

const ONBOARDING_TIPS = [
  { text: "Bienvenue sur CardFlow ! Découvrez toutes les fonctionnalités en quelques étapes.", highlight: null, title: "Tour des fonctionnalités" },
  { text: "Glissez des éléments depuis la barre d'outils a gauche sur le canvas. Basculez entre Éléments et Web Blocs.", highlight: "toolbox-sidebar", title: "Barre d'outils", position: "right" },
  { text: "Cliquez sur une carte pour editer ses propriétés : titre, montant, description, tags, pièces jointes.", highlight: null, title: "Panneau propriétés" },
  { text: "Connectéz deux cartes en glissant entre les points de connexion. Double-cliquez un connectéur pour changer sa couleur.", highlight: null, title: "Connexions & couleurs" },
  { text: "Organisez votre projet en plusieurs pages avec des onglets. Visualisez l'architecture globale en un clic.", highlight: "page-tabs", title: "Multi-pages", position: "bottom" },
  { text: "L'Assistant IA créé vos schémas automatiquement. Dictez ou ecrivez votre projet, il genere les pages et connexions.", highlight: "assistant-btn", title: "Assistant IA", position: "bottom" },
  { text: "Générez un Cahier des Charges professionnel en PDF avec le schéma en annexe.", highlight: "summary-btn", title: "Cahier des charges", position: "bottom" },
  { text: "Lancez une présentation diapo avec voix off et enregistrement video.", highlight: "presentation-btn", title: "Présentation & Review", position: "bottom" },
  { text: "Gérez les acces et clés API requis pour le projet. Le client les fournit via le Portail Client.", highlight: "accesses-btn", title: "Accès & Portail", position: "bottom" },
  { text: "Vous etes pret ! Commencez par ajouter des éléments ou utilisez l'Assistant IA pour generer votre premier schema.", highlight: null, title: "C'est parti !" },
];

function getTargetRect(testId) {
  if (!testId) return null;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

export function OnboardingTour({ step, setStep, presenting }) {
  const [targetRect, setTargetRect] = useState(null);
  const cardRef = useRef(null);

  // Update target rect when step changes
  useEffect(() => {
    if (step < 0 || step >= ONBOARDING_TIPS.length) {
      setTargetRect(null);
      return;
    }
    const tip = ONBOARDING_TIPS[step];
    if (!tip.highlight) {
      setTargetRect(null);
      return;
    }
    // Small delay to let elements render
    const timer = setTimeout(() => {
      const rect = getTargetRect(tip.highlight);
      setTargetRect(rect);
    }, 150);
    return () => clearTimeout(timer);
  }, [step]);

  if (presenting) return null;

  const tip = step >= 0 && step < ONBOARDING_TIPS.length ? ONBOARDING_TIPS[step] : null;

  // Calculate card position relative to target
  const getCardStyle = () => {
    if (!targetRect || !tip) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

    const pad = 16;
    const cardW = 360;
    const cardH = 240;
    const pos = tip.position || "bottom";

    if (pos === "right") {
      return {
        top: `${Math.max(80, targetRect.top)}px`,
        left: `${targetRect.right + pad}px`,
      };
    }
    if (pos === "left") {
      return {
        top: `${Math.max(80, targetRect.top)}px`,
        left: `${Math.max(pad, targetRect.left - cardW - pad)}px`,
      };
    }
    // bottom (default for toolbar items)
    return {
      top: `${targetRect.bottom + pad}px`,
      left: `${Math.max(pad, Math.min(targetRect.left + targetRect.width / 2 - cardW / 2, window.innerWidth - cardW - pad))}px`,
    };
  };

  return (
    <>
      {/* Tour overlay */}
      {tip && (
        <div className="fixed inset-0 z-[60]" data-testid="onboarding-tour">
          {/* Dark backdrop with spotlight cutout */}
          {targetRect ? (
            <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
              <defs>
                <mask id="spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={targetRect.left - 6}
                    y={targetRect.top - 6}
                    width={targetRect.width + 12}
                    height={targetRect.height + 12}
                    rx="10"
                    fill="black"
                  />
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#spotlight-mask)" />
              {/* Highlight border around target */}
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 6}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx="10"
                fill="none"
                stroke="#818CF8"
                strokeWidth="2"
                strokeDasharray="6 3"
              >
                <animate attributeName="stroke-dashoffset" values="18;0" dur="1s" repeatCount="indefinite" />
              </rect>
            </svg>
          ) : (
            <div className="absolute inset-0 bg-black/50" />
          )}

          {/* Click-to-close backdrop area (not on card) */}
          <div className="absolute inset-0" onClick={() => setStep(-1)} />

          {/* Tour card */}
          <div
            ref={cardRef}
            className="fixed animate-in fade-in zoom-in-95 duration-200"
            style={getCardStyle()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-[360px] overflow-hidden">
              {/* Progress bar */}
              <div className="h-1 bg-gray-100">
                <div className="h-full bg-indigo-600 transition-all duration-300" style={{ width: `${((step + 1) / ONBOARDING_TIPS.length) * 100}%` }} />
              </div>
              <div className="p-5">
                {/* Header with close button */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                      {step + 1}
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-gray-900">{tip.title}</h3>
                      <span className="text-[10px] text-gray-400">{step + 1} sur {ONBOARDING_TIPS.length}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setStep(-1)}
                    className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                    data-testid="close-tour"
                  >
                    <X size={15} />
                  </button>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  {tip.text}
                </p>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {step > 0 && (
                      <button
                        onClick={() => setStep(s => s - 1)}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-1"
                        data-testid="prev-tour"
                      >
                        <ChevronLeft size={12} /> Précédent
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (step < ONBOARDING_TIPS.length - 1) {
                        setStep(s => s + 1);
                      } else {
                        setStep(-1);
                      }
                    }}
                    className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-[11px] font-bold hover:bg-indigo-700 transition-colors flex items-center gap-1"
                    data-testid="next-tour"
                  >
                    {step < ONBOARDING_TIPS.length - 1 ? "Suivant" : "Terminer"} <ChevronRight size={12} />
                  </button>
                </div>

                {/* Step dots */}
                <div className="flex items-center justify-center gap-1 mt-3">
                  {ONBOARDING_TIPS.map((_, i) => (
                    <div key={i} className={`rounded-full transition-all ${i === step ? "w-4 h-1.5 bg-indigo-600" : i < step ? "w-1.5 h-1.5 bg-indigo-300" : "w-1.5 h-1.5 bg-gray-200"}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating replay button — right edge */}
      {step < 0 && (
        <button
          onClick={() => setStep(0)}
          className="fixed top-1/2 -translate-y-1/2 right-0 z-[45] bg-indigo-600 text-white pl-2.5 pr-1.5 py-2 rounded-l-xl shadow-lg hover:bg-indigo-700 hover:pr-2.5 transition-all flex items-center gap-1.5 group opacity-70 hover:opacity-100"
          title="Revoir le tour des fonctionnalités"
          data-testid="tour-fab"
        >
          <HelpCircle size={14} className="group-hover:rotate-12 transition-transform shrink-0" />
          <span className="text-[9px] font-bold hidden group-hover:inline whitespace-nowrap">Guide</span>
        </button>
      )}
    </>
  );
}
